import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PageTitleWrapper from '@/components/PageTitleWrapper';
import { useUserViewMode } from '@/hooks/useUserViewMode';
import { headerBranchId } from '../food-delivery/helpers';
import { branchService } from '@/data/branchService';
import { foodDeliveryService } from '@/data/foodDeliveryService';
import {
  BranchMonitoring,
  EXTERNAL_INTEGRATION_STATUS_LABELS,
  EXTERNAL_SYNC_STATUS_LABELS,
  ExternalIntegrationStatus,
  FOOD_ALERT_CODE_LABELS,
  FOOD_ALERT_SEVERITY_COLOR,
  FOOD_PROVIDER_LABELS
} from '@/types/FoodDelivery.interface';
import FoodDeliveryEmptyState from '../food-delivery/components/FoodDeliveryEmptyState';
import { Branch } from '@/types/Branch.interface';

const WINDOW_OPTIONS = [
  { value: 24, label: 'Son 24 saat' },
  { value: 72, label: 'Son 3 gün' },
  { value: 168, label: 'Son 7 gün' }
];

/** "Siparişleri geri getir" penceresi — backend en fazla 24 saate izin verir. */
const BACKFILL_OPTIONS = [
  { value: 1, label: 'Son 1 saat' },
  { value: 2, label: 'Son 2 saat' },
  { value: 6, label: 'Son 6 saat' },
  { value: 24, label: 'Son 24 saat' }
];

const INTEGRATION_STATUS_COLOR: Record<
  ExternalIntegrationStatus,
  'success' | 'error' | 'warning' | 'default' | 'info'
> = {
  [ExternalIntegrationStatus.ACTIVE]: 'success',
  [ExternalIntegrationStatus.ERROR]: 'error',
  [ExternalIntegrationStatus.PAUSED]: 'warning',
  [ExternalIntegrationStatus.PENDING]: 'info',
  [ExternalIntegrationStatus.DISABLED]: 'default'
};

const fmtDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString('tr-TR') : '—';

const fmtPct = (rate: number | null): string =>
  rate === null ? '—' : `%${Math.round(rate * 100)}`;

// Küçük metrik kutusu (sayı + etiket).
const Stat: React.FC<{
  label: string;
  value: React.ReactNode;
  color?: string;
}> = ({ label, value, color }) => (
  <Box sx={{ textAlign: 'center', px: 2, py: 1 }}>
    <Typography variant="h3" sx={{ color }}>
      {value}
    </Typography>
    <Typography variant="caption" color="textSecondary">
      {label}
    </Typography>
  </Box>
);

const FoodDeliveryMonitoring: React.FC = () => {
  const { isBranchAdmin, isAdminView, currentBranch } = useUserViewMode();

  const [branches, setBranches] = useState<Branch[]>([]);
  const headerBranch = headerBranchId(currentBranch, isAdminView);
  const [branchId, setBranchId] = useState<number | null>(headerBranch);
  // Üst menüden şube değişince sayfa da o şubeye geçer.
  useEffect(() => {
    if (headerBranch) setBranchId(headerBranch);
  }, [headerBranch]);
  const [windowHours, setWindowHours] = useState<number>(24);
  const [data, setData] = useState<BranchMonitoring | null>(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{
    msg: string;
    severity: 'success' | 'error' | 'info';
  } | null>(null);
  const [backfillHours, setBackfillHours] = useState<number>(2);
  const [backfillingId, setBackfillingId] = useState<number | null>(null);

  // Şube listesi (admin/şirket görünümü).
  useEffect(() => {
    if (isBranchAdmin) return;
    branchService
      .getAllFlat({ getAll: true })
      .then(setBranches)
      .catch(() =>
        setToast({ msg: 'Şubeler getirilemedi.', severity: 'error' })
      );
  }, [isBranchAdmin]);

  const load = useCallback(async () => {
    if (!branchId) {
      setData(null);
      return;
    }
    setLoading(true);
    try {
      const res = await foodDeliveryService.getMonitoring(
        branchId,
        windowHours
      );
      setData(res);
    } catch {
      setToast({ msg: 'Sağlık verileri getirilemedi.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [branchId, windowHours]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Kesinti sonrası kurtarma: normal yoklama yalnız son 30 dakikaya bakar.
   * Uzun kesintide (deploy, ağ arızası, platform kesintisi) aradaki siparişler
   * hiç çekilmez — bu aksiyon o pencereyi elle geri getirir.
   */
  const handleBackfill = async (integrationId: number) => {
    setBackfillingId(integrationId);
    try {
      const res = await foodDeliveryService.backfillOrders(
        integrationId,
        backfillHours
      );
      setToast({
        msg:
          `Geri getirme tamamlandı: ${res.packages} paket işlendi ` +
          `(${res.windows} saatlik dilim tarandı).`,
        severity: 'success'
      });
      await load();
    } catch {
      setToast({ msg: 'Siparişler geri getirilemedi.', severity: 'error' });
    } finally {
      setBackfillingId(null);
    }
  };

  const overview = data?.overview;
  const metrics = data?.metrics;
  const alerts = data?.alerts ?? [];

  return (
    <Box sx={{ width: '100%' }}>
      <PageTitleWrapper>
        <Box>
          <Typography variant="h3" component="h3" gutterBottom>
            Sağlık & İzleme
          </Typography>
          <Typography variant="subtitle2">
            Yemek platformu entegrasyonlarının sağlığı, sipariş/webhook
            metrikleri ve uyarılar. Kritik uyarılar arka planda periyodik olarak
            da değerlendirilir.
          </Typography>
        </Box>
      </PageTitleWrapper>

      <Box sx={{ px: 4, pb: 4 }}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ md: 'center' }}
            >
              {!isBranchAdmin && (
                <Autocomplete
                  sx={{ minWidth: 280 }}
                  options={branches}
                  getOptionLabel={(b) => b.name}
                  value={branches.find((b) => b.id === branchId) ?? null}
                  onChange={(_e, val) => setBranchId(val?.id ?? null)}
                  renderInput={(params) => (
                    <TextField {...params} label="Şube seç" />
                  )}
                />
              )}
              <TextField
                select
                label="Zaman aralığı"
                value={windowHours}
                onChange={(e) => setWindowHours(Number(e.target.value))}
                sx={{ minWidth: 180 }}
              >
                {WINDOW_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <Box sx={{ flexGrow: 1 }} />
              <Tooltip title="Yenile">
                <span>
                  <IconButton onClick={load} disabled={!branchId || loading}>
                    {loading ? <CircularProgress size={22} /> : <RefreshIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </CardContent>
        </Card>

        {!branchId ? (
          <Alert severity="info">İzleme için bir şube seçin.</Alert>
        ) : loading && !data ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Uyarılar */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h4" gutterBottom>
                  Uyarılar ({alerts.length})
                </Typography>
                {alerts.length === 0 ? (
                  <Alert
                    icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                    severity="success"
                  >
                    Aktif uyarı yok. Tüm entegrasyonlar sağlıklı görünüyor.
                  </Alert>
                ) : (
                  <Stack spacing={1}>
                    {alerts.map((a, idx) => (
                      <Alert
                        key={`${a.code}-${a.integrationId ?? 'branch'}-${idx}`}
                        severity={FOOD_ALERT_SEVERITY_COLOR[a.severity]}
                      >
                        <AlertTitle sx={{ mb: 0 }}>
                          {FOOD_ALERT_CODE_LABELS[a.code]}
                          {a.integrationId
                            ? ` · entegrasyon #${a.integrationId}`
                            : ''}
                        </AlertTitle>
                        {a.message}
                      </Alert>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>

            {/* Entegrasyon sağlık özeti */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={1}
                  sx={{ mb: 1 }}
                >
                  <Typography variant="h4">
                    Entegrasyonlar ({overview?.summary.total ?? 0})
                  </Typography>
                  <TextField
                    select
                    size="small"
                    label="Geri getirme aralığı"
                    value={backfillHours}
                    onChange={(e) => setBackfillHours(Number(e.target.value))}
                    sx={{ minWidth: 190 }}
                    helperText="Kesinti sonrası kullanılır"
                  >
                    {BACKFILL_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                {!overview || overview.integrations.length === 0 ? (
                  <FoodDeliveryEmptyState
                    title="İzlenecek entegrasyon yok"
                    message="Bu şubede yemek platformu entegrasyonu yok; izleme metrikleri de oluşmaz. Önce bir entegrasyon oluşturun."
                  />
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Platform</TableCell>
                          <TableCell>Durum</TableCell>
                          <TableCell>Son Sağlıklı</TableCell>
                          <TableCell>Son Yoklama</TableCell>
                          <TableCell>Son Hata</TableCell>
                          <TableCell align="right">İşlem</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {overview.integrations.map((i) => (
                          <TableRow key={i.id} hover>
                            <TableCell>
                              {FOOD_PROVIDER_LABELS[i.providerCode]}
                              {i.displayName ? ` — ${i.displayName}` : ''}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                color={INTEGRATION_STATUS_COLOR[i.status]}
                                label={
                                  EXTERNAL_INTEGRATION_STATUS_LABELS[i.status]
                                }
                              />
                            </TableCell>
                            <TableCell>{fmtDate(i.lastHealthyAt)}</TableCell>
                            {/* TGO webhook GÖNDERMEZ; anlamlı olan yoklamadır. */}
                            <TableCell>{fmtDate(i.lastPolledAt)}</TableCell>
                            <TableCell>
                              {i.lastErrorMessage ? (
                                <Tooltip title={i.lastErrorMessage}>
                                  <span>{fmtDate(i.lastErrorAt)}</span>
                                </Tooltip>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip
                                title={
                                  'Bu entegrasyonun son ' +
                                  `${backfillHours} saatteki siparişlerini ` +
                                  'platformdan yeniden çeker. Kesinti sonrası ' +
                                  'kaçan siparişler için kullanılır; mevcut ' +
                                  'kayıtları çoğaltmaz.'
                                }
                              >
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<HistoryIcon />}
                                    disabled={backfillingId !== null}
                                    onClick={() => handleBackfill(i.id)}
                                  >
                                    {backfillingId === i.id
                                      ? 'Getiriliyor…'
                                      : 'Siparişleri Geri Getir'}
                                  </Button>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>

            {/* Metrikler */}
            {metrics && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h4" gutterBottom>
                        Siparişler
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          sx={{ ml: 1 }}
                        >
                          (dönüşüm {fmtPct(metrics.conversionRate)})
                        </Typography>
                      </Typography>
                      <Stack
                        direction="row"
                        flexWrap="wrap"
                        justifyContent="space-around"
                      >
                        <Stat label="Toplam" value={metrics.orders.total} />
                        <Stat
                          label="Dönüştü"
                          value={metrics.orders.converted}
                          color="green"
                        />
                        <Stat
                          label="Manuel"
                          value={metrics.orders.manualReview}
                          color="orange"
                        />
                        <Stat label="Red" value={metrics.orders.rejected} />
                        <Stat label="İptal" value={metrics.orders.canceled} />
                        <Stat
                          label="Hatalı"
                          value={metrics.orders.failed}
                          color="red"
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h4" gutterBottom>
                        Webhook
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          sx={{ ml: 1 }}
                        >
                          (hata oranı {fmtPct(metrics.webhookFailureRate)})
                        </Typography>
                      </Typography>
                      <Stack
                        direction="row"
                        flexWrap="wrap"
                        justifyContent="space-around"
                      >
                        <Stat label="Toplam" value={metrics.webhooks.total} />
                        <Stat
                          label="Doğrulandı"
                          value={metrics.webhooks.verified}
                        />
                        <Stat
                          label="Tekrar"
                          value={metrics.webhooks.duplicate}
                        />
                        <Stat
                          label="Reddedildi"
                          value={metrics.webhooks.rejected}
                        />
                        <Stat
                          label="Hatalı"
                          value={metrics.webhooks.failed}
                          color="red"
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h4" gutterBottom>
                        Bekleyen Kuyruklar
                      </Typography>
                      <Stack direction="row" justifyContent="space-around">
                        <Stat
                          label="Dead-letter"
                          value={metrics.deadLetterBacklog}
                          color={
                            metrics.deadLetterBacklog > 0 ? 'red' : undefined
                          }
                        />
                        <Stat
                          label="Manuel inceleme"
                          value={metrics.manualReviewBacklog}
                          color={
                            metrics.manualReviewBacklog > 0
                              ? 'orange'
                              : undefined
                          }
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h4" gutterBottom>
                        Fiyat/Uygunluk Senkronu
                      </Typography>
                      <Stack spacing={1}>
                        <Typography variant="body2">
                          Son durum:{' '}
                          {metrics.sync.lastStatus ? (
                            <Chip
                              size="small"
                              label={
                                EXTERNAL_SYNC_STATUS_LABELS[
                                  metrics.sync.lastStatus
                                ]
                              }
                              color={
                                metrics.sync.lastStatus === 'FAILED'
                                  ? 'error'
                                  : 'default'
                              }
                            />
                          ) : (
                            '—'
                          )}
                        </Typography>
                        <Typography variant="body2">
                          Son çalışma: {fmtDate(metrics.sync.lastAt)}
                        </Typography>
                        <Typography variant="body2">
                          Pencerede başarısız: {metrics.sync.failedCount}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </>
        )}
      </Box>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            onClose={() => setToast(null)}
            variant="filled"
          >
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default FoodDeliveryMonitoring;
